import React, { useEffect } from 'react'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Pagination, RowsSkeletonLoader } from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { useGetLedgerWalletTransactions } from 'hooks/queries/Ledger/useGetLedgerWalletTransactions'
import { useRewardsPortfoliosQuery } from 'hooks/queries/Rewards/useRewardsPortfoliosQuery'
import { useRewardsProductsQuery } from 'hooks/queries/Rewards/useRewardsProductsQuery'
import { usePaginationPagingPersist } from 'hooks/usePaginationPagingPersist'
import { rewardsDetailsBreadcrumbAtom } from './atoms/breadcrumbAtom'
import { RewardsBalanceCard } from './components/RewardsBalanceCard'
import { RewardsCryptoPerformance } from './components/RewardsCryptoPerformance'
import { RewardsMaximizeCard } from './components/RewardsMaximizeCard'
import { RewardsPageHeader } from './components/RewardsPageHeader'
import { RewardsTransactionsList } from './components/RewardsTransactionsList'
import { RewardsSheetProvider } from './context/RewardsSheetProvider'
import useRewardsTracking from './hooks/useRewardsTracking'
import { useTransactionsPagination } from './hooks/useTransactionsPagination'
import { formatAPY } from './utils'

const DEFAULT_NUMBER_OF_ROWS = 100

export const RewardsDetails = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { trackPageView } = useRewardsTracking()
  const setRewardsDetailsBreadcrumb = useSetAtom(rewardsDetailsBreadcrumbAtom)

  useEffect(() => {
    trackPageView('details')
  }, [])

  const { currency } = useParams<{ currency: string }>()

  const { data: rewardsProductsData, isLoading: isLoadingProducts } = useRewardsProductsQuery()
  const rewardsProducts = rewardsProductsData?.content ?? []

  const { data: rewardsPortfolios, isLoading: isLoadingPortfolios } = useRewardsPortfoliosQuery()
  const rewardsPortfoliosData = rewardsPortfolios?.content ?? []
  const portfolioForCurrency = rewardsPortfoliosData.find(
    portfolio => portfolio.currency === currency
  )
  const hasPortfolioForCurrency = !!portfolioForCurrency

  const { page, handleChangePage } = useTransactionsPagination()
  const { data: transactionsData, isLoading: isLoadingTransactions } =
    useGetLedgerWalletTransactions({
      walletReference: portfolioForCurrency?.walletId,
      enabled: hasPortfolioForCurrency,
      page,
      size: Number(DEFAULT_NUMBER_OF_ROWS),
    })

  const transactions = transactionsData?.content ?? []
  const { paging } = usePaginationPagingPersist(transactionsData?.pagination)
  const showPagination =
    transactions.length > 0 && paging?.total / DEFAULT_NUMBER_OF_ROWS > 1 && !isLoadingTransactions

  const product = rewardsProducts.find(p => p.currency === currency)

  useEffect(() => {
    setRewardsDetailsBreadcrumb(currency)
    return () => {
      setRewardsDetailsBreadcrumb('')
    }
  }, [currency])

  if (!product) {
    navigate(PathNames.NOT_FOUND)
    return null
  }

  if (isLoadingProducts || isLoadingPortfolios) {
    return <RowsSkeletonLoader rows={10} />
  }

  return (
    <RewardsSheetProvider
      userPortfolios={rewardsPortfoliosData}
      rewardsProducts={rewardsProducts}
      selectedCurrency={product.currency}
    >
      <RewardsPageHeader
        description={t('rewards.detailsPageDescription', {
          apy: formatAPY(product.earningRate),
          currency: product.currency,
        })}
        showWithdrawButton={hasPortfolioForCurrency}
      />

      <Box flexCol gapY={24}>
        <Box gap={16} width="full" className="grid grid-cols-1 lg:grid-cols-[1fr_3fr]">
          <RewardsBalanceCard
            currency={product.currency}
            balance={portfolioForCurrency?.balance.total ?? '0.00'}
            allTimeEarnings={portfolioForCurrency?.totalEarnedAmount ?? '0'}
          />
          <RewardsMaximizeCard currency={product.currency} maxMonthlyRewards="$10,000" />
        </Box>

        {hasPortfolioForCurrency && (
          <RewardsCryptoPerformance
            loading={isLoadingTransactions}
            currency={product.currency}
            transactions={transactions}
          />
        )}

        <RewardsTransactionsList loading={isLoadingTransactions} transactions={transactions} />

        {showPagination && (
          <Pagination
            testId="earnings-pagination"
            page={page}
            rowsPerPage={DEFAULT_NUMBER_OF_ROWS}
            total={paging?.total ?? 0}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={undefined} // Prevent changing rows per page
          />
        )}
      </Box>
    </RewardsSheetProvider>
  )
}
